import React, { useState } from "react";
import { MoreVertical, Trash2, Star, Menu, X, ChevronLeft } from "lucide-react";
import EmotionRing from "./EmotionRing";
import { useLanguage } from "../../context/LanguageContext";

import { MOODS } from "../../lib/constants";
export default function ChatHeader({
  guide,
  isOnline = false,
  isConnected = true,
  onClear,
  isArchived = false,
  onToggleArchive,
  onRate,
  currentMood,
  onUpdateMood,
  onToggleSidebar,
  isSidebarOpen = false,
  onBack = null
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  const otherMoodName = guide?.otherUser?.currentMood || guide?.otherUser?.moods?.[0]?.mood || "neutral";
  const userMood = MOODS.find(m => m.id === otherMoodName || m.label === otherMoodName) || MOODS.find(m => m.id === "neutral");

  return (
    <div className="flex flex-col border-b border-gray-200 dark:border-gray-700 py-3 mb-2 px-2 sm:px-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Mobile Navigation: either Back (Chevron) or Menu */}
          <button
            onClick={onBack || onToggleSidebar}
            className="md:hidden p-2 -ml-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            {onBack ? <ChevronLeft className="w-6 h-6" /> : (isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />)}
          </button>
          {/* Avatar with optional Mood Ring */}
          <div className="relative shrink-0 ml-1">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg overflow-hidden transition-all duration-300 ${!guide?.otherUser?.avatar ? "text-brand-blue bg-blue-50 dark:bg-gray-800" : ""}`}
              title={guide?.otherUser?.currentMood ? `Current Mood: ${guide.otherUser.currentMood}` : ""}
            >
              {guide?.otherUser?.avatar ? (
                <img src={guide.otherUser.avatar} alt="avatar" className="w-full h-full rounded-full object-cover" />
              ) : (
                (guide?.title || guide?.name || "?").charAt(0).toUpperCase()
              )}
            </div>

            {/* Formatted Mood Ring using MOODS constants */}
            {userMood && (
              <div className={`absolute -inset-[3px] rounded-full border-[2.5px] ${userMood.border} opacity-80 pointer-events-none transition-all duration-300`} />
            )}

            {isConnected && isOnline && (
              <span className="absolute -bottom-0.5 -right-0.5 z-10 w-3.5 h-3.5 bg-green-500 border-2 border-white dark:border-gray-900 rounded-full"></span>
            )}
          </div>

          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2 truncate max-w-[140px] sm:max-w-none">
              <span className="truncate">{guide?.title || guide?.name}</span>
              {!isConnected && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 animate-pulse">
                  Reconnecting...
                </span>
              )}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              {isConnected && (
                <span className={`text-[10px] uppercase font-bold tracking-wider ${isOnline ? "text-green-600 dark:text-green-400" : "text-gray-400 dark:text-gray-500"}`}>
                  {isOnline ? t('dash_online') : t('dash_offline')}
                </span>
              )}
              {userMood && (
                <>
                  <span className="text-[10px] text-gray-300 dark:text-gray-600">•</span>
                  <span className={`text-[10px] font-bold uppercase tracking-widest ${userMood.color}`}>
                    {t(`mood_${userMood.id}`) || userMood.label}
                  </span>
                </>
              )}
              <span className="hidden sm:inline text-[10px] text-gray-400 dark:text-gray-500">•</span>
              <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400">
                {t('chat_privacy_notice')}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 relative">
          {onUpdateMood && (
            <div className="mr-1 mt-1">
              <EmotionRing
                currentMood={currentMood || "calm"}
                onChange={onUpdateMood}
                size="sm"
              />
            </div>
          )}

          <button
            onClick={() => setOpen((v) => !v)}
            className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
          >
            <MoreVertical className="w-5 h-5" />
          </button>

          {open && (
            <>
              {/* Backdrop for mobile choice capture */}
              <div className="fixed inset-0 z-40 " onClick={() => setOpen(false)} />
              <div className="absolute right-0 top-10 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <button
                  onClick={() => {
                    setOpen(false);
                    onToggleArchive?.();
                  }}
                  className="flex items-center gap-2 w-full px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-left"
                >
                  <Trash2 className="w-4 h-4 text-gray-400" />
                  {isArchived ? t('chat_unarchive_action') : t('chat_archive_action')}
                </button>

                {onRate && (
                  <button
                    onClick={() => {
                      setOpen(false);
                      onRate();
                    }}
                    className="flex items-center gap-2 w-full px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 text-brand-purple text-left"
                  >
                    <Star className="w-4 h-4 text-brand-purple" />
                    Rate Provider
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
